import React, { useCallback, useState } from 'react';

import StaggeredPackageDescription from './components/staggered-package-description/staggered-package-description';
import TireWheelPackageDescription from './components/tire-wheel-package-description/tire-wheel-package-description';
import BundleItemProductDescription from '../bundle-item-product-description/bundle-item-product-description';
import { ACTIONS } from '../modify-entry-confirmation/modify-entry-confirmation';

import { isArrayWithLength } from '../../shared/utils/validators/validators';

import {
  IncompletePackageActionType,
  IncompletePackageGroupEntry
} from '../../shared/types/product';
import { ENTRY_TYPES } from '../../shared/constants/cart';

interface BundleProductDescriptionProps {
  groupEntry: IncompletePackageGroupEntry;
  hasRequireConfirmation?: boolean;
  hideChangeProduct?: boolean;
  hideLabel?: boolean;
  hideProductType?: boolean;
  isReadOnly?: boolean;
  onChangeProductInBundle: (action?: IncompletePackageActionType) => void;
  shouldShowCartPrice?: boolean;
}

function BundleProductDescription(props: BundleProductDescriptionProps) {
  const {
    groupEntry,
    hasRequireConfirmation,
    hideChangeProduct,
    hideLabel,
    hideProductType,
    isReadOnly,
    onChangeProductInBundle,
    shouldShowCartPrice = false
  } = props;

  const { entries } = groupEntry || {};
  const [pendingAction, setPendingAction] =
    useState<IncompletePackageActionType>({});

  const onChangeProduct = useCallback(
    (actionType, entryIndex) => {
      const action = {
        entryIndex,
        groupEntry,
        type: actionType
      };

      if (hasRequireConfirmation) {
        setPendingAction(action);
      } else {
        onChangeProductInBundle(action);
      }
    },
    [groupEntry, onChangeProductInBundle, hasRequireConfirmation]
  );

  const onConfirmChangeProduct = useCallback(() => {
    onChangeProductInBundle(pendingAction);
  }, [pendingAction, onChangeProductInBundle]);

  const onCancel = useCallback(() => {
    setPendingAction({});
  }, []);

  if (!isArrayWithLength(entries)) {
    return null;
  }

  if (groupEntry.entryType === ENTRY_TYPES.PACKAGE) {
    return (
      <TireWheelPackageDescription
        groupEntry={groupEntry}
        hideChangeProduct={hideChangeProduct}
        isReadOnly={isReadOnly}
        onCancel={onCancel}
        onChangeProduct={onChangeProduct}
        onChangeProductInBundle={onChangeProductInBundle}
        onConfirmChangeProduct={onConfirmChangeProduct}
        pendingAction={pendingAction}
        shouldShowCartPrice={shouldShowCartPrice}
      />
    );
  }

  if (
    groupEntry.entryType === ENTRY_TYPES.STAGGERED &&
    entries &&
    isArrayWithLength(entries)
  ) {
    return (
      <StaggeredPackageDescription
        groupEntry={groupEntry}
        hideChangeProduct={hideChangeProduct}
        hideProductType={hideProductType}
        onCancel={onCancel}
        onChangeProduct={onChangeProduct}
        onConfirmChangeProduct={onConfirmChangeProduct}
        pendingAction={pendingAction}
        shouldShowCartPrice={shouldShowCartPrice}
      />
    );
  }

  return (
    <BundleItemProductDescription
      entry={entries?.[0]}
      hideChangeProduct={isReadOnly || hideChangeProduct}
      hideLabel={hideLabel}
      hideLogo
      isPriceForEach
      onChangeProduct={() => onChangeProduct(ACTIONS.CHANGE, 0)}
      shouldShowCartPrice={shouldShowCartPrice}
      showMinimumPriceMessage
    />
  );
}

export default BundleProductDescription;
